import { t } from '../../lib/i18n';
import { type DocumentsFilter } from './types';

export interface FilterTab {
  key: DocumentsFilter;
  count: number;
}

interface FilterTabsProps {
  filter: DocumentsFilter;
  tabs: FilterTab[];
  onChange: (filter: DocumentsFilter) => void;
}

/**
 * Status filter — `Redesign-D-Admin{,-Mobile}`, screen «Документи».
 *
 * Two shapes for one control, exactly as the artboards draw them: a segmented
 * control sitting in a `cf-fill` trough from `lg` up, and below it a row of
 * pill chips that scroll horizontally and bleed into the shell's 16px gutter
 * (`-mx-4 px-4`), so the fourth tab is visibly cut off rather than wrapped —
 * that overflow is what tells the operator to swipe.
 *
 * The count is part of the label in both, because the number is the reason a
 * moderator picks a tab at all.
 */
export function FilterTabs({ filter, tabs, onChange }: FilterTabsProps) {
  return (
    <>
      {/* ── Mobile: scrolling pills ─────────────────────────────────────── */}
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:hidden">
        {tabs.map((tab) => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.key)}
              className={`h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap ${
                active
                  ? 'border-cf-brand bg-cf-brand text-white'
                  : 'border-cf-line bg-cf-surface text-cf-ink'
              }`}
            >
              {t(`verification.admin.docs.tabs.${tab.key}`)} · {tab.count}
            </button>
          );
        })}
      </div>

      {/* ── Desktop: segmented control ──────────────────────────────────── */}
      <div
        role="tablist"
        className="hidden w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] lg:flex"
      >
        {tabs.map((tab) => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.key)}
              className={`h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap ${
                active ? 'bg-cf-brand text-white' : 'bg-transparent text-cf-ink'
              }`}
            >
              {t(`verification.admin.docs.tabs.${tab.key}`)}{' '}
              <span className="font-mono text-[11px] font-medium opacity-70">{tab.count}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
